"use client";

import { type RefObject, useLayoutEffect, useState } from "react";
import {
	type PipCoordinateOffset,
	resolvePipCoordinateOffset,
} from "./pip-stage-coordinates";

const ZERO_OFFSET: PipCoordinateOffset = { x: 0, y: 0 };

export function usePipCoordinateBridge(
	boundsStageRef: RefObject<HTMLElement | null>,
	portalStageRef: RefObject<HTMLElement | null> | undefined,
	enabled: boolean,
): PipCoordinateOffset {
	const [offset, setOffset] = useState<PipCoordinateOffset>(ZERO_OFFSET);

	useLayoutEffect(() => {
		if (!enabled) {
			setOffset(ZERO_OFFSET);
			return;
		}

		const boundsStage = boundsStageRef.current;
		const portalStage = portalStageRef?.current ?? boundsStageRef.current;
		if (!boundsStage || !portalStage) {
			setOffset(ZERO_OFFSET);
			return;
		}

		const sync = () => {
			setOffset(resolvePipCoordinateOffset(boundsStage, portalStage));
		};

		sync();

		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", sync);
			return () => window.removeEventListener("resize", sync);
		}

		const observer = new ResizeObserver(sync);
		observer.observe(boundsStage);
		observer.observe(portalStage);
		window.addEventListener("resize", sync);
		window.addEventListener("scroll", sync, true);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", sync);
			window.removeEventListener("scroll", sync, true);
		};
	}, [boundsStageRef, enabled, portalStageRef]);

	return offset;
}
